import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TokenStorageService } from '../services/token-storage.service';

let isRefreshing = false;
const refreshedTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenStorage = inject(TokenStorageService);
  const authService = inject(AuthService);
  const router = inject(Router);

  const isAuthEndpoint = req.url.includes('/auth/login');

  const accessToken = tokenStorage.getAccessToken();
  const authorizedReq = accessToken && !isAuthEndpoint
    ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
    : req;

  return next(authorizedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      const isUnauthorized = error.status === 401;
      const isRefreshEndpoint = req.url.includes('/auth/refresh');

      if (!isUnauthorized || isRefreshEndpoint || isAuthEndpoint) {
        return throwError(() => error);
      }

      if (isRefreshing) {
        return refreshedTokenSubject.pipe(
          filter((token) => token !== null),
          take(1),
          switchMap((token) =>
            next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })),
          ),
        );
      }

      isRefreshing = true;
      refreshedTokenSubject.next(null);

      return authService.refresh().pipe(
        switchMap((response) => {
          isRefreshing = false;
          refreshedTokenSubject.next(response.accessToken);
          return next(
            req.clone({ setHeaders: { Authorization: `Bearer ${response.accessToken}` } }),
          );
        }),
        catchError((refreshError) => {
          isRefreshing = false;
          authService.forceLocalLogout();
          router.navigateByUrl('/login');
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
